import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BriefcaseBusiness,
  Clock3,
  Filter,
  Home,
  PackageCheck,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  UserRound,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import "./CoursesPage.css";
import "./FreelancerServicesPage.css";
import {
  formatServiceCurrency,
  getFreelancerServiceSummary,
  getFreelancerServiceTitle,
  getFreelancerProfileReference,
  getServiceFreelancer,
  getServiceDeliveryLabel,
  getServiceRevisionsLabel,
  listFreelancerServices,
} from "../api/freelancerServices";

const serviceFallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80";

function ServiceCard({ service, isAr }) {
  const title = getFreelancerServiceTitle(service, isAr);
  const summary = getFreelancerServiceSummary(service, isAr);
  const image = service?.cover_image || serviceFallbackImage;
  const ratingValue = Number(service?.average_rating || service?.avg_rating || 0);
  const orders = Number(service?.orders_count || 0);

  const serviceRef = String(service?.slug || service?.id || "").trim();
  const detailRef = serviceRef && serviceRef !== "html" ? serviceRef : service?.id;
  const freelancer = getServiceFreelancer(service);
  const profileRef = getFreelancerProfileReference(service);

  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;
    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = serviceFallbackImage;
  };

  return (
    <div className="courses-page-card-wrap">
      <article className="courses-page-card">
        <Link to={`/freelancers/${detailRef}`} className="courses-page-card__media" aria-label={title}>
          <img src={image} alt={title} className="courses-page-card__image" onError={handleImageError} />
          <span className="courses-page-card__shade" />
          <div className="courses-page-card__badges">
            <span className="courses-page-pill courses-page-pill--light">
              <Clock3 size={14} />
              {getServiceDeliveryLabel(service, isAr)}
            </span>
            <span className="courses-page-pill courses-page-pill--price">
              {formatServiceCurrency(service?.price || 0, service?.currency || "USD")}
            </span>
          </div>
        </Link>

        <div className="courses-page-card__content">
          <div className="courses-page-card__topline">
            <span>
              <Sparkles size={14} />
              {service?.category || (isAr ? "خدمة احترافية" : "Professional Service")}
            </span>
            <span>
              <Star size={14} />
              {ratingValue ? ratingValue.toFixed(1) : "0.0"}
            </span>
          </div>

          <h3 className="courses-page-card__title">
            <Link to={`/freelancers/${detailRef}`}>{title}</Link>
          </h3>
          <p className="courses-page-card__description">{summary}</p>

          <div className="courses-page-card__facts">
            <div>
              <PackageCheck size={16} />
              <span>
                {orders} {isAr ? "طلب" : "orders"}
              </span>
            </div>
            <div>
              <RefreshCw size={16} />
              <span>{getServiceRevisionsLabel(service, isAr)}</span>
            </div>
            <div>
              <BriefcaseBusiness size={16} />
              <span>{service?.category || (isAr ? "عام" : "General")}</span>
            </div>
          </div>

          <div className="courses-page-card__footer">
            <div className="courses-page-card__instructor">
              <UserRound size={16} />
              {profileRef ? (
                <Link
                  to={`/freelancers/profile/${encodeURIComponent(profileRef)}`}
                  state={{ freelancer, service }}
                  className="freelancer-profile-link"
                >
                  {freelancer?.name || (isAr ? "مستقل" : "Freelancer")}
                </Link>
              ) : (
                <span>{freelancer?.name || (isAr ? "مستقل" : "Freelancer")}</span>
              )}
            </div>
            <Link to={`/freelancers/${detailRef}`} className="courses-page-card__button">
              <span>{isAr ? "عرض التفاصيل" : "View Details"}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}

export default function FreelancerServicesPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [category, setCategory] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [rating, setRating] = useState(0);
  const [ordersCount, setOrdersCount] = useState("");

  const resetFilters = () => {
    setSearch("");
    setPriceRange([0, 10000]);
    setCategory("");
    setDeliveryTime("");
    setRating(0);
    setOrdersCount("");
  };

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const data = await listFreelancerServices({ cache: false });
        if (active) setServices(data);
      } catch (err) {
        logger.error("Freelancer services load error", err);
        if (active) {
          setError(isAr ? "فشل تحميل خدمات المستقلين." : "Failed to load freelancer services.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isAr]);

  const filteredServices = useMemo(() => {
    let result = services;
    const query = search.trim().toLowerCase();

    if (query) {
      result = result.filter((service) => {
        const haystack = [
          service?.title_ar,
          service?.title_en,
          service?.short_description_ar,
          service?.short_description_en,
          service?.description_ar,
          service?.description_en,
          service?.category,
          service?.freelancer?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });
    }

    result = result.filter((service) => {
      const price = Number(service?.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (category) {
      result = result.filter((service) => service?.category === category);
    }

    if (deliveryTime) {
      result = result.filter((service) => Number(service?.delivery_days || 0) <= Number(deliveryTime));
    }

    if (rating > 0) {
      result = result.filter((service) => Number(service?.average_rating || service?.avg_rating || 0) >= rating);
    }

    if (ordersCount) {
      result = result.filter((service) => Number(service?.orders_count || 0) >= Number(ordersCount));
    }

    return result;
  }, [services, search, priceRange, category, deliveryTime, rating, ordersCount]);

  const pageStats = useMemo(() => {
    const categories = new Set(services.map((item) => item?.category).filter(Boolean));
    const orders = services.reduce((total, item) => total + Number(item?.orders_count || 0), 0);
    const fast = services.filter((item) => Number(item?.delivery_days || 0) > 0 && Number(item?.delivery_days || 0) <= 3).length;

    return [
      { key: "items", value: services.length, label: isAr ? "خدمة" : "Services" },
      { key: "categories", value: categories.size, label: isAr ? "تخصص" : "Categories" },
      { key: "orders", value: orders, label: isAr ? "طلب" : "Orders" },
      { key: "fast", value: fast, label: isAr ? "تسليم سريع" : "Fast Delivery" },
    ];
  }, [services, isAr]);

  const hasActiveFilters =
    search.trim() ||
    priceRange[0] !== 0 ||
    priceRange[1] !== 10000 ||
    category ||
    deliveryTime ||
    rating > 0 ||
    ordersCount;

  return (
    <div className="courses-page-v2 freelancer-page-v2" dir={isAr ? "rtl" : "ltr"}>
      <div className="courses-page-shell">
        <nav className="courses-page-breadcrumb" aria-label="breadcrumb">
          <Link to="/">
            <Home size={17} />
            {isAr ? "الرئيسية" : "Home"}
          </Link>
          <span>/</span>
          <strong>{isAr ? "خدمات المستقلين" : "Freelancer Services"}</strong>
        </nav>

        <section className="courses-page-hero">
          <div className="courses-page-hero__copy">
            <span className="courses-page-hero__eyebrow">
              <Sparkles size={16} />
              {isAr ? "خدمات رقمية جاهزة" : "Ready Digital Services"}
            </span>
            <h1>{isAr ? "اكتشف خدمات مستقلة باحترافية وتجربة حديثة" : "Discover freelancer services in a polished, modern experience"}</h1>
            <p>
              {isAr
                ? "ابحث عن الخدمة المناسبة، قارن التسليم والسعر والتقييم، ثم انتقل للتفاصيل والطلب بسهولة."
                : "Find the right service, compare delivery, price, and ratings, then move to details and ordering with ease."}
            </p>

            <div className="courses-page-search-panel">
              {/* <div className="courses-page-search-panel__head">
                <span>
                  <Search size={18} />
                  {isAr ? "ابحث في الخدمات" : "Search Services"}
                </span>
                <small>
                  {filteredServices.length} {isAr ? "نتيجة" : "results"}
                </small>
              </div> */}
              <label className="courses-page-search">
                <Search size={19} />
                <input
                  type="search"
                  placeholder={isAr ? "اسم الخدمة، المستقل، أو التخصص" : "Service name, freelancer, or category"}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div className="courses-page-search-panel__hint">
                <span>
                  {services.length} {isAr ? "خدمة إجمالًا" : "total services"}
                </span>
                {hasActiveFilters ? (
                  <button type="button" onClick={resetFilters}>
                    <RotateCcw size={14} />
                    {isAr ? "تصفير" : "Reset"}
                  </button>
                ) : null}
              </div>
            </div>

            {/* <div className="courses-page-hero__stats" aria-label={isAr ? "إحصائيات الخدمات" : "Service statistics"}>
              {pageStats.map((stat) => (
                <div key={stat.key}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div> */}
          </div>
        </section>

        <section className="courses-page-filters" aria-label={isAr ? "فلاتر الخدمات" : "Service filters"}>
          <div className="courses-page-filters__title">
            <span>
              <Filter size={18} />
              {isAr ? "فلترة النتائج" : "Filter Results"}
            </span>
            <button type="button" onClick={resetFilters}>
              <RotateCcw size={15} />
              {isAr ? "إعادة تعيين" : "Reset All"}
            </button>
          </div>

          <div className="freelancer-page-filters-grid courses-page-filters__grid">
            <div className="courses-page-filter">
              <label>{isAr ? "السعر" : "Price"}</label>
              <div className="courses-page-filter__split">
                <input
                  type="number"
                  placeholder={isAr ? "أدنى" : "Min"}
                  value={priceRange[0] === 0 ? "" : priceRange[0]}
                  onChange={(event) => setPriceRange([Number(event.target.value || 0), priceRange[1]])}
                />
                <input
                  type="number"
                  placeholder={isAr ? "أقصى" : "Max"}
                  value={priceRange[1] === 10000 ? "" : priceRange[1]}
                  onChange={(event) => setPriceRange([priceRange[0], Number(event.target.value || 10000)])}
                />
              </div>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التخصص" : "Category"}</label>
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">{isAr ? "الكل" : "All"}</option>
                <option value="Design">{isAr ? "التصميم" : "Design"}</option>
                <option value="Development">{isAr ? "التطوير" : "Development"}</option>
                <option value="Marketing">{isAr ? "التسويق" : "Marketing"}</option>
                <option value="Writing">{isAr ? "الكتابة" : "Writing"}</option>
                <option value="Video">{isAr ? "الفيديو والرسوميات" : "Video & Animation"}</option>
                <option value="Audio">{isAr ? "الموسيقى والصوت" : "Music & Audio"}</option>
                <option value="Business">{isAr ? "الأعمال" : "Business"}</option>
                <option value="Data">{isAr ? "البيانات" : "Data"}</option>
                <option value="Photography">{isAr ? "التصوير" : "Photography"}</option>
                <option value="AI">{isAr ? "خدمات الذكاء الاصطناعي" : "AI Services"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التسليم" : "Delivery"}</label>
              <select value={deliveryTime} onChange={(event) => setDeliveryTime(event.target.value)}>
                <option value="">{isAr ? "أي وقت" : "Any"}</option>
                <option value="1">{isAr ? "1 يوم" : "1 day"}</option>
                <option value="3">{isAr ? "3 أيام" : "3 days"}</option>
                <option value="7">{isAr ? "7 أيام" : "7 days"}</option>
                <option value="14">{isAr ? "14 يوم" : "14 days"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التقييم" : "Rating"}</label>
              <select value={rating} onChange={(event) => setRating(Number(event.target.value))}>
                <option value="0">{isAr ? "الكل" : "All"}</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
                <option value="5">5</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "الطلبات" : "Orders"}</label>
              <select value={ordersCount} onChange={(event) => setOrdersCount(event.target.value)}>
                <option value="">{isAr ? "أي عدد" : "Any"}</option>
                <option value="10">10+</option>
                <option value="50">50+</option>
                <option value="100">100+</option>
              </select>
            </div>
          </div>
        </section>

        <div className="courses-page-section-head">
          <div>
            <span>{isAr ? "خدمات مميزة" : "Featured Services"}</span>
            <h2>{isAr ? "الخدمات المتاحة" : "Available Services"}</h2>
          </div>
          <p>
            {/* {isAr
              ? "بطاقات حديثة تعرض أهم تفاصيل الخدمة قبل الانتقال إلى صفحة الطلب."
              : "Modern cards showing the key service details before moving to the order page."} */}
          </p>
        </div>

        {loading ? (
          <div className="courses-page-state">
            <div className="spinner-border text-primary mb-3" role="status" />
            <p>{isAr ? "جاري تحميل الخدمات..." : "Loading services..."}</p>
          </div>
        ) : error ? (
          <div className="courses-page-alert">{error}</div>
        ) : filteredServices.length === 0 ? (
          <div className="courses-page-empty">
            <Search size={34} />
            <h2>{isAr ? "لا توجد خدمات مطابقة" : "No matching services"}</h2>
            <p>{isAr ? "جرّب تغيير كلمة البحث أو تصفير الفلاتر." : "Try a different search term or reset the filters."}</p>
            <button type="button" onClick={resetFilters}>{isAr ? "إعادة تعيين الفلاتر" : "Reset Filters"}</button>
          </div>
        ) : (
          <div className="courses-page-grid">
            {filteredServices.map((service) => (
              <ServiceCard key={service.id} service={service} isAr={isAr} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
